<template>
  <div v-if="isEnabled" class="form-captcha">
    <div ref="captcha" class="captcha-container" />
    <p v-if="debug" class="captcha-debug" :data-status="status">
      Turnstile: {{ statusLabel }}
      <template v-if="token"> (token {{ token.length }} chars)</template>
      <template v-else-if="error"> — {{ error }}</template>
    </p>
  </div>
</template>

<script setup>
import { useField } from "vee-validate";

const captcha = ref(null);
const widgetId = ref(null);
const error = ref(null);
const status = ref("idle"); // idle | loading | ready | ok | error

const config = useRuntimeConfig();
const route = useRoute();
const sitekey = config.public["turnstileSitekey"];
// Turnstile expects lowercase codes (e.g. "fr"), not BCP-47 like "fr-FR"
const language = String(config.public["language"] || "auto")
  .toLowerCase()
  .split("-")[0];
const { load: loadTurnstile } = useTurnstileScript();

const debug =
  config.public["turnstileDebug"] === true ||
  config.public["turnstileDebug"] === "true" ||
  route.query.turnstileDebug === "1" ||
  route.query.turnstileDebug === "true";

const rules = () => !error.value || error.value;

const { value: token } = useField("captchaToken", rules);

const isEnabled = !!sitekey;

const statusLabel = computed(() => {
  switch (status.value) {
    case "loading":
      return "loading script…";
    case "ready":
      return "widget ready, waiting for challenge…";
    case "ok":
      return "OK — challenge passed";
    case "error":
      return "error";
    default:
      return "idle";
  }
});

const renderWidget = () => {
  if (widgetId.value || !captcha.value || !window.turnstile) return;

  status.value = "ready";

  // After async script load, call render() directly — turnstile.ready() forbids async/defer.
  // appearance: "always" keeps the widget visible (debug); "interaction-only" hides when auto-passed.
  widgetId.value = window.turnstile.render(captcha.value, {
    sitekey,
    language,
    appearance: debug ? "always" : "interaction-only",
    "response-field-name": "captchaToken",
    callback: (t) => {
      token.value = t;
      error.value = null;
      status.value = "ok";
    },
    "error-callback": (e) => {
      error.value = e;
      token.value = null;
      status.value = "error";
    },
  });
};

onMounted(async () => {
  await nextTick();

  if (!isEnabled || !captcha.value) return;

  status.value = "loading";

  try {
    await loadTurnstile();
  } catch {
    error.value = "turnstile_load_failed";
    status.value = "error";
    return;
  }

  renderWidget();
});

defineExpose({
  reset: () => {
    if (sitekey && widgetId.value && window.turnstile) {
      window.turnstile.reset(widgetId.value);
      status.value = "ready";
      token.value = null;
      error.value = null;
    }
  },
});
</script>

<style lang="scss" scoped>
.form-captcha {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.captcha-debug {
  margin: 0;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  line-height: 1.3;
  border-radius: 0.25rem;
  background: #f3f4f6;
  color: #374151;

  &[data-status="ok"] {
    background: #dcfce7;
    color: #166534;
  }

  &[data-status="error"] {
    background: #fee2e2;
    color: #991b1b;
  }

  &[data-status="loading"],
  &[data-status="ready"] {
    background: #fef9c3;
    color: #854d0e;
  }
}
</style>
